import { useEffect, useRef, useState } from "react";
import { crearPedido, crearRenglonPedido } from "../services/pedidosService";
import { ESTADO_INICIAL_PEDIDO, TIPO_PEDIDO } from "../constants/pedido";
import { puedeGuardar } from "../utils/lineas";

const PROGRESO_INICIAL = {
  snapshot: null, // { pedido, renglones } que se envía; los reintentos usan el mismo.
  idPedido: null, // insertId una vez creado el pedido.
  renglonesGuardados: 0,
};

// Habilitación del botón "Guardar" y guardado del pedido + renglones.
//
// No hay transacción en el backend, así que si el pedido se crea pero falla
// algún renglón el guardado queda "parcial": al reintentar no se crea otro
// pedido (mismo uuid), sólo se reenvían los renglones pendientes del snapshot
// original.
export const useGuardarPedido = ({
  uuidPedido,
  clienteSeleccionado,
  direccionSeleccionada,
  datosPedido,
  pedidoParaCambio,
  datosRenglon,
  onGuardado,
  onError,
}) => {
  const [disabled, setDisabled] = useState(false);
  const [guardadoParcial, setGuardadoParcial] = useState(false);
  const progresoRef = useRef({ ...PROGRESO_INICIAL });
  // Candado síncrono contra doble clic: "disabled" recién bloquea el botón en
  // el siguiente render, el ref corta la segunda llamada al instante.
  const enviandoRef = useRef(false);

  useEffect(() => {
    setDisabled(!puedeGuardar(clienteSeleccionado, direccionSeleccionada, datosRenglon));
  }, [clienteSeleccionado, direccionSeleccionada, datosPedido, datosRenglon]);

  const armarSnapshot = () => {
    const usuario = localStorage.getItem("user");
    return {
      pedido: {
        tipo: TIPO_PEDIDO,
        id_direccion: direccionSeleccionada.id,
        fecha_para: datosPedido.fecha_para,
        observaciones: datosPedido.observaciones,
        uuid_pedido: uuidPedido,
        estado: ESTADO_INICIAL_PEDIDO,
        usuario,
        pedido_cambio: pedidoParaCambio,
      },
      renglones: datosRenglon.map((linea) => ({
        uuid_pedido: uuidPedido,
        producto: linea.producto,
        cantidad: linea.cantidad,
        especificaciones: linea.especificaciones,
        usuario,
        promo: linea.promo,
        entrega_sena: linea.sena,
      })),
    };
  };

  const guardarPedido = async () => {
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setDisabled(true);
    const progreso = progresoRef.current;
    if (!progreso.snapshot) {
      progreso.snapshot = armarSnapshot();
    }
    const { pedido, renglones } = progreso.snapshot;

    try {
      if (progreso.idPedido === null) {
        const data = await crearPedido(pedido);
        progreso.idPedido = data.insertId;
      }

      for (let i = progreso.renglonesGuardados; i < renglones.length; i++) {
        await crearRenglonPedido(renglones[i]);
        progreso.renglonesGuardados = i + 1;
      }

      onGuardado();
    } catch (err) {
      console.error("Error guardando pedido:", err);

      if (progreso.idPedido === null) {
        // Nada quedó guardado: se descarta el snapshot para que un reintento
        // tome los datos que el usuario tenga en pantalla.
        progreso.snapshot = null;
        onError("No se pudo crear el pedido. Intente nuevamente.");
      } else {
        setGuardadoParcial(true);
        onError(
          `El pedido #${progreso.idPedido} se creó, pero falló el renglón ${
            progreso.renglonesGuardados + 1
          } de ${renglones.length}. Presione "Guardar" para reintentar los renglones pendientes.`,
        );
      }
      // Sólo se libera ante un error, para permitir el reintento. Si se guardó
      // bien queda bloqueado hasta que se cierre el modal.
      enviandoRef.current = false;
      setDisabled(false);
    }
  };

  return {
    disabled,
    guardarPedido,
    guardadoParcial,
    idPedidoParcial: progresoRef.current.idPedido,
  };
};
